// Cookie consent helper (ISO 27001 / GDPR aligned).
//
// All third-party cookies on this site are loaded through Google Tag Manager
// (GTM-TCCZVLF): Google Analytics, Facebook Pixel and Microsoft Clarity.
//
// Google Consent Mode alone does NOT stop cookies from being written, so we
// take a stricter approach: GTM is NOT loaded automatically. It is injected
// only after the user accepts. When the user rejects, we also delete the
// first-party tracking cookies we are allowed to remove.

export const CONSENT_KEY = 'smile_cookie_consent';

export const CONSENT_ACCEPTED = 'accepted';
export const CONSENT_REJECTED = 'rejected';

const GTM_ID = 'GTM-TCCZVLF';

// Prefixes of the (first-party) tracking cookies we are able to delete from JS.
// Cross-domain cookies (.facebook.com, .clarity.ms) and HttpOnly cookies cannot
// be removed by client-side script: they are prevented by NOT loading GTM.
const TRACKING_COOKIE_PREFIXES = ['_ga', '_gid', '_gat', '_gcl', '_fbp', '_fbc', '_clck', '_clsk'];

// Define gtag/dataLayer and register the default "denied" state.
// This lives in the bundled app ('self') instead of an inline <script> so it
// is not blocked by a CSP without 'unsafe-inline'/nonce. It runs before GTM is
// ever injected, so Google tags always see the denied default first.
const bootstrapConsentMode = () => {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    // eslint-disable-next-line func-names, prefer-rest-params
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
  if (!window.__consentDefaultSet) {
    window.__consentDefaultSet = true;
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
      wait_for_update: 500,
    });
  }
};

// Read the stored decision. Returns null when the user has not chosen yet.
export const getConsent = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(CONSENT_KEY));
    return stored && stored.value ? stored.value : null;
  } catch (error) {
    return null;
  }
};

// Inject the official GTM snippet a single time.
export const loadGtm = () => {
  if (window.__gtmLoaded) return;
  window.__gtmLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
};

// Build the list of domains a first-party cookie might be scoped to.
const cookieDomains = () => {
  const { hostname } = window.location;
  const domains = ['', hostname, `.${hostname}`];
  const parts = hostname.split('.');
  if (parts.length > 2) {
    domains.push(`.${parts.slice(-2).join('.')}`); // e.g. .smileweb.net
  }
  return domains;
};

// Expire a cookie across every domain/path combination it could live under.
const deleteCookie = (name) => {
  cookieDomains().forEach((domain) => {
    const domainPart = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
  });
};

// Remove every first-party tracking cookie currently present.
export const deleteTrackingCookies = () => {
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name && TRACKING_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))) {
      deleteCookie(name);
    }
  });
};

// Notify Google Consent Mode about the decision (for tags that honor it).
const updateGoogleConsent = (granted) => {
  const state = granted ? 'granted' : 'denied';

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state,
    });
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'cookie_consent_update',
    cookie_consent: granted ? CONSENT_ACCEPTED : CONSENT_REJECTED,
  });
};

// Apply a decision: load or block GTM, clean up cookies, update consent mode.
const applyConsent = (granted) => {
  bootstrapConsentMode();
  updateGoogleConsent(granted);

  if (granted) {
    loadGtm();
  } else {
    deleteTrackingCookies();
  }
};

// Persist the decision and apply its effects.
export const setConsent = (value) => {
  const granted = value === CONSENT_ACCEPTED;

  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ value, date: new Date().toISOString() }),
    );
  } catch (error) {
    // localStorage may be unavailable (private mode); consent stays denied.
  }

  applyConsent(granted);
};

// Run on every page load: restore the previous decision's effects.
// - accepted  -> (re)load GTM
// - rejected  -> keep tracking cookies cleared
// - undecided -> do nothing (the banner will be shown)
export const initConsent = () => {
  bootstrapConsentMode();

  const consent = getConsent();
  if (consent === CONSENT_ACCEPTED) {
    applyConsent(true);
  } else if (consent === CONSENT_REJECTED) {
    applyConsent(false);
  }
  return consent;
};

export default getConsent;
